import { useCallback, useEffect, useState } from "react";
import { Dayjs } from "dayjs";
import { getDashboardSaleSummary } from "../../services";
import { DashboardSaleSummary } from "../../types/dashboard.type";
import { formatNumber } from "../../utils";
import Paper from "@mui/material/Paper";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import { NET_LABEL, hasNoteAmounts } from "../CreditDebitNote/reportFigures";
import NoteCaptions from "./NoteCaptions";

interface SalesTargetSummaryProps {
  selectedDate: Dayjs | null;
}

export default function SalesTargetSummary({
  selectedDate,
}: SalesTargetSummaryProps) {
  const [saleSummary, setSaleSummary] = useState<DashboardSaleSummary>();

  const onGetSaleSummary = useCallback(async (date: Dayjs) => {
    const {
      data: { data },
    } = await getDashboardSaleSummary(date.format("YYYY-MM-DD"));
    setSaleSummary(data);
  }, []);

  useEffect(() => {
    if (selectedDate) onGetSaleSummary(selectedDate);
  }, [selectedDate, onGetSaleSummary]);

  if (!saleSummary) return <Paper sx={{ height: "100%" }} />;

  const isOverTarget =
    saleSummary.total_sales_amount > saleSummary.sales_target;
  const hasNotes = hasNoteAmounts(
    saleSummary.credit_note_amount,
    saleSummary.debit_note_amount,
  );

  return (
    <Paper
      sx={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Typography variant="body1" fontWeight={600} mb={2}>
        สรุปยอดขายวันนี้
      </Typography>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          gap: 1.5,
        }}
      >
        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            overflow: "hidden",
            bgcolor: (theme) => theme.palette.primary.main + "1A",
            border: (theme) => `2px solid ${theme.palette.primary.main}`,
            borderRadius: 3,
            p: 1.5,
          }}
        >
          <Typography color="text.secondary" variant="body1" mb={0.5}>
            เป้ายอดขาย
          </Typography>
          <Typography variant="h6" fontWeight={700}>
            ฿{formatNumber(saleSummary.sales_target)}
          </Typography>
        </Box>
        <Box
          sx={{
            flex: "1 1 0",
            minHeight: 0,
            overflow: "hidden",
            bgcolor: (theme) => theme.palette.primary.main + "1A",
            border: (theme) => `2px solid ${theme.palette.primary.main}`,
            borderRadius: 3,
            p: 1.5,
          }}
        >
          <Typography color="text.secondary" variant="body1" mb={0.5}>
            ยอดขายวันนี้
          </Typography>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-end"
            gap={1}
          >
            <Typography variant="h6" fontWeight={700}>
              ฿{formatNumber(saleSummary.total_sales_amount)}
              {hasNotes && (
                <Typography
                  component="span"
                  variant="caption"
                  color="text.secondary"
                  sx={{ ml: 0.5 }}
                >
                  {NET_LABEL}
                </Typography>
              )}
            </Typography>
            <NoteCaptions
              creditNoteAmount={saleSummary.credit_note_amount}
              debitNoteAmount={saleSummary.debit_note_amount}
            />
          </Stack>
          <Box display="flex" alignItems="center" mt={0.75}>
            <Chip
              size="small"
              label={`${isOverTarget ? "+" : ""}${
                saleSummary.percent_compare_sales_target
              }%`}
              sx={{
                bgcolor: (theme) =>
                  isOverTarget
                    ? theme.palette.success.main
                    : theme.palette.error.main,
                color: "#fff",
                fontWeight: 600,
                borderRadius: 3,
                mr: 1,
              }}
            />
            <Typography color="text.secondary" variant="caption">
              จากเป้ายอดขาย
            </Typography>
          </Box>
        </Box>
      </Box>
    </Paper>
  );
}
